// Subscription Service for Club Tier Payments
// Handles recurring subscription payments via Stripe

import { STRIPE_CONFIG } from '../config/stripe';

// Get API base URL for Stripe subscription endpoints.
// - If REACT_APP_STRIPE_API_URL is set (e.g. Lambda): use it; path will be /stripe/create-subscription.
// - If REACT_APP_VERCEL_URL is set: use it (for local dev pointing to deployed Vercel).
// - If unset on web: use same-origin /api (Vercel serverless at /api/stripe/create-subscription).
//   - In localhost, fallback to deployed Vercel URL if REACT_APP_VERCEL_URL is not set.
// - If unset on native: keep placeholder (subscriptions are web-only).
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_STRIPE_API_URL) {
    return process.env.REACT_APP_STRIPE_API_URL;
  }
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isChessmapsRoot = hostname === 'chessmaps.app';
    const resolvedOrigin = isChessmapsRoot ? 'https://www.chessmaps.app' : window.location.origin;
    // In localhost, Vercel serverless functions don't work - use deployed URL or vercel dev
    if (isLocalhost) {
      // Use REACT_APP_VERCEL_URL if set, otherwise use production domain (www to avoid redirect)
      const deployedUrl = process.env.REACT_APP_VERCEL_URL || 'https://www.chessmaps.app';
      return deployedUrl + '/api';
    }
    // In production, normalize chessmaps.app to www to avoid 307 redirect
    return resolvedOrigin + '/api';
  }
  return 'https://your-api-gateway-url.execute-api.us-east-1.amazonaws.com/dev';
};

/**
 * Create a Stripe subscription for Club tier
 * @param {string} userId - User/Club ID
 * @param {string} email - User email
 * @param {string} clubName - Club name
 * @param {string} priceId - Stripe price ID for the subscription (e.g., price_xxx for $30/month)
 * @returns {Promise} Subscription details including clientSecret for payment
 */
export const createClubSubscription = async (userId, email, clubName, priceId = null) => {
  // Validate required parameters
  if (!email || (typeof email === 'string' && email.trim() === '')) {
    const error = new Error('Email is required to create a subscription');
    throw error;
  }
  
  try {
    const apiBaseUrl = getApiBaseUrl();
    const fullUrl = `${apiBaseUrl}/stripe/create-subscription`;
    const requestBody = {
      userId,
      email,
      clubName,
      priceId, // If null, backend will create a default $30/month price
      tier: 'CLUB',
      currency: STRIPE_CONFIG.currency || 'usd',
    };
    let response;
    try {
      response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
    } catch (fetchError) {
      // Enhanced error message for network failures
      const enhancedError = new Error(
        `Network error: Failed to reach ${fullUrl}. ` +
        `This could mean: (1) The endpoint doesn't exist, (2) CORS is blocking the request, ` +
        `(3) The serverless function isn't deployed. ` +
        `Original error: ${fetchError.message}`
      );
      enhancedError.cause = fetchError;
      throw enhancedError;
    }
    
    if (!response.ok) {
      let errorData = {};
      let errorText = '';
      try {
        errorText = await response.text();
        errorData = JSON.parse(errorText);
      } catch (parseError) {
      }
      // Include request body in error for debugging
      const errorMessage = errorData.message || errorText || 'Failed to create subscription';
      const error = new Error(`${errorMessage} (Request body had email: ${!!requestBody.email})`);
      error.status = response.status;
      error.errorData = errorData;
      throw error;
    }
    
    const data = await response.json();
    return {
      subscriptionId: data.subscriptionId,
      clientSecret: data.clientSecret,
      customerId: data.customerId,
    };
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

/**
 * Cancel a subscription
 * @param {string} subscriptionId - Stripe subscription ID
 * @param {boolean} immediately - Cancel immediately or at period end
 * @param {boolean} reactivate - Reactivate a subscription set to cancel
 * @returns {Promise}
 */
export const cancelClubSubscription = async (subscriptionId, immediately = false, reactivate = false) => {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/stripe/cancel-subscription`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscriptionId,
          immediately,
          reactivate,
        }),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to cancel subscription');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};

/**
 * Get subscription status
 * @param {string} userId - User/Club ID
 * @param {string} email - User email (optional, helps find customer)
 * @returns {Promise} Subscription status and details
 */
export const getSubscriptionStatus = async (userId, email = null) => {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/stripe/subscription-status`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          email,
        }),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to get subscription status');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting subscription status:', error);
    throw error;
  }
};

/**
 * Update payment method for a subscription
 * @param {string} subscriptionId - Stripe subscription ID
 * @param {string} paymentMethodId - New payment method ID
 * @returns {Promise}
 */
export const updateSubscriptionPaymentMethod = async (subscriptionId, paymentMethodId) => {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/stripe/update-payment-method`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscriptionId,
          paymentMethodId,
        }),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to update payment method');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating payment method:', error);
    throw error;
  }
};

/**
 * Get upcoming invoice for a subscription
 * @param {string} subscriptionId - Stripe subscription ID
 * @returns {Promise} Invoice details
 */
export const getUpcomingInvoice = async (subscriptionId) => {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/stripe/upcoming-invoice`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscriptionId,
        }),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to get upcoming invoice');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting upcoming invoice:', error);
    throw error;
  }
};

export default {
  createClubSubscription,
  cancelClubSubscription,
  getSubscriptionStatus,
  updateSubscriptionPaymentMethod,
  getUpcomingInvoice,
};



